import { useState } from "react";
import LogManager from "../../../utils/logManager";
import { useInsightPolling, useInsightSeed, useAddedHighlight } from "../insights.hooks";
import { LOAD_ERROR_MESSAGE } from "./actionItems.constants";
import { POLL_INTERVAL_MS } from "../insights.constants";
import {
  createActionItem,
  amendActionItem,
  buildItemFields,
  fetchActionItemsSeed,
} from "./actionItems.utils";
import { parseLambdaPayload } from "../../../utils/routerLambdaUtils";

/**
 * useActionItems - Action-items-specific configuration of the shared useInsightPolling hook.
 *
 * Fetches from /action-items on mount, prepends+dedups newly polled items, and sends a
 * `since` cursor on poll requests. Skips the initial fetch when the store is already seeded
 * (e.g. by Home.jsx's badge-count fetch).
 */
export const useActionItems = (meetingUuid, insightStore) =>
  useInsightPolling({
    meetingUuid,
    insightStore,
    endpoint: "/action-items",
    pollIntervalMs: POLL_INTERVAL_MS,
    storeKey: "actionItems",
    responseKey: "actionItems",
    upsertKey: "upsertActionItems",
    errorLabel: "Failed to fetch action items:",
    pollErrorLabel: "Fallback poll for action items failed:",
    errorMessage: LOAD_ERROR_MESSAGE,
    initialIsLoading: true,
    initialHasFetched: false,
    requireNonEmpty: true,
    skipInitialFetchIfSeeded: true,
    useSince: true,
  });

/**
 * useActionItemsSeed - Action-items-specific configuration of the shared useInsightSeed hook.
 * Gated on the Action Items feature flag so users without the feature never issue this request.
 */
export const useActionItemsSeed = ({
  meetingUuid,
  insightStore,
  isSessionActive,
  isTranscriptOnly,
  isEnabled,
}) =>
  useInsightSeed({
    meetingUuid,
    insightStore,
    isSessionActive,
    isTranscriptOnly,
    isEnabled,
    fetchItems: fetchActionItemsSeed,
    upsertItems: (items) => insightStore.upsertActionItems(items),
    errorLabel: "Failed to seed initial action items:",
  });

/**
 * useActionItemMutations - Optimistic add / update / delete for action items.
 *
 * All writes go through the insight store's functional upsert/remove (the single source of
 * truth), so the list and the header badge stay consistent and a concurrent fetch/push can never
 * evict the optimistic row. The change applies immediately, then persists via POST /action-items;
 * on success the server row reconciles in place (same itemId). On a failed update/delete the
 * optimistic change is kept and the error logged; realtime WS pushes reconcile it. A failed add
 * reverts its optimistic row (see addItem).
 *
 * `update` is intentionally last-write-wins: it sends no version guard, so the most recent write
 * to land wins (matching how Agenda behaves). This is a deliberate downgrade — the optimistic-
 * concurrency / conflict-notice UX was removed and will be redesigned in a future error-handling
 * CR; it is NOT an oversight.
 */
export const useActionItemMutations = ({ meetingUuid, insightStore }) => {
  // POST a write to /action-items and decode the RouterLambda envelope via the shared
  // parseLambdaPayload helper (same double-encoded contract as the seed/poll fetches, so the
  // decode lives in one place). A backend-signalled error (data.error) is thrown with its code
  // attached so callers can branch — e.g. on "NOT_FOUND".
  const postActionItem = async (body) => {
    const response = await fetch("/action-items", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    let data;
    try {
      data = await parseLambdaPayload(response, `/action-items ${body.operation}`);
    } catch (err) {
      // parseLambdaPayload only embeds the HTTP status in its message string, so attach the numeric
      // fetch Response.status for callers that must branch on a definite client reject (HTTP 400)
      // vs a maybe-persisted failure. Additive: existing update/delete callers branch on err.code.
      err.status = response.status;
      throw err;
    }
    if (data?.error) {
      const err = new Error(`/action-items ${body.operation} error: ${data.error}`);
      err.code = data.error; // e.g. "NOT_FOUND" — callers branch on it.
      err.status = response.status; // 2xx here — the error is signalled in the payload, not the status.
      throw err;
    }
    return data;
  };

  const updateItem = (itemId, fields) => {
    // Optimistic overlay: merge the edited fields onto the existing row (updatedAt refreshed,
    // detectedAt preserved by the store merge — see amendActionItem).
    insightStore.upsertActionItems([amendActionItem(itemId, fields)]);

    // Persist last-write-wins: no expectedVersion is sent, so the backend falls back to
    // last-write-wins (the version guard / conflict UX was removed; this is a deliberate downgrade,
    // matching Agenda). Route the fields through buildItemFields so the write allowlist matches the
    // optimistic row's.
    postActionItem({
      operation: "update",
      meetingUuid,
      itemId,
      ...buildItemFields(fields),
    })
      .then((data) => {
        if (data?.actionItem) {
          insightStore.upsertActionItems([data.actionItem], { force: true }); // authoritative server row
        }
      })
      .catch((e) => {
        // Generic failure: the optimistic row is kept; WS pushes reconcile it.
        LogManager.error("[ActionItems] update failed (optimistic row kept):", e);
      });
  };

  const deleteItem = (itemId) => {
    insightStore.removeActionItem(itemId);

    postActionItem({ operation: "delete", meetingUuid, itemId })
      .catch((e) => LogManager.error("[ActionItems] delete failed (optimistic removal kept):", e));
  };

  const addItem = (fields) => {
    // Mint the row (client-side itemId + timestamps + amendmentCount) via createActionItem, then
    // send that SAME itemId to the backend so the optimistic row, the server write, and the eventual
    // WS `created` push all share one id. Without this, a `created` push arriving before the POST
    // response wouldn't match the temp row and would insert a duplicate. The backend keys the item
    // on this id (attribute_not_exists guard also makes a retried add idempotent).
    const optimistic = createActionItem(fields);
    // upsert prepends the new row newest-first; a concurrent fetch/push can't evict it.
    insightStore.upsertActionItems([optimistic]);

    postActionItem({ operation: "add", meetingUuid, itemId: optimistic.itemId, ...buildItemFields(fields) })
      .then((data) => {
        if (data?.actionItem) {
          // Reconcile with the server row (same itemId, canonical fields/version).
          insightStore.upsertActionItems([data.actionItem], { force: true }); // authoritative server row
        }
      })
      .catch((e) => {
        // Drop the optimistic row rather than leave an orphan that looks saved but isn't.
        // Only a definite client reject (HTTP 400) tombstones the id: the server refused the
        // payload, so the add definitely did NOT persist and no WS `created` will ever arrive —
        // tombstoning safely blocks a lagging poll from resurrecting the phantom row. For ANY
        // other failure (5xx / network / timeout / unknown) the add MAY have persisted server-side,
        // so remove WITHOUT tombstoning — otherwise the ~60s tombstone would suppress the genuine
        // WS `created` push and a truly-saved item wouldn't appear until the tombstone expired.
        // User-facing failure messaging + retry is deferred to the error-handling CR.
        insightStore.removeActionItem(optimistic.itemId, { tombstone: e.status === 400 });
        LogManager.error("[ActionItems] add failed, reverted optimistic row:", e);
      });

    return optimistic;
  };

  return { updateItem, deleteItem, addItem };
};

/**
 * useActionItemActions - Add/edit/delete UI state and handlers, layered over useActionItemMutations.
 * Edit and add forms stay open until their own save/cancel, so several can be open at once; the
 * delete confirmation is single-row and any other action cancels it.
 */
export const useActionItemActions = ({ meetingUuid, insightStore }) => {
  const [editingIds, setEditingIds] = useState(() => new Set());
  const [isAdding, setIsAdding] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const { highlightId, flash } = useAddedHighlight();
  const { updateItem, deleteItem, addItem } = useActionItemMutations({ meetingUuid, insightStore });

  const openEdit = (itemId) =>
    setEditingIds((prev) => (prev.has(itemId) ? prev : new Set(prev).add(itemId)));
  const closeEdit = (itemId) =>
    setEditingIds((prev) => {
      if (!prev.has(itemId)) return prev;
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });

  const startEdit = (item) => {
    setPendingDeleteId(null); // another action cancels an open delete confirmation
    openEdit(item.itemId);
  };

  const saveEdit = (itemId, fields) => {
    setPendingDeleteId(null);
    updateItem(itemId, fields);
    closeEdit(itemId);
  };

  const cancelEdit = (itemId) => {
    setPendingDeleteId(null);
    closeEdit(itemId);
  };

  const startAdd = () => {
    setPendingDeleteId(null);
    setIsAdding(true);
  };

  const saveAdd = (fields) => {
    setPendingDeleteId(null);
    const created = addItem(fields);
    setIsAdding(false);
    flash(created.itemId);
  };

  const cancelAdd = () => {
    setPendingDeleteId(null);
    setIsAdding(false);
  };

  // Delete confirmation — transient and single-row. Starting it on another row (or the delete
  // itself) replaces/clears the current one; opening an edit/add also clears it (see above).
  const startDelete = (item) => setPendingDeleteId(item.itemId);
  const confirmDelete = (item) => {
    deleteItem(item.itemId);
    setPendingDeleteId(null);
  };
  const cancelDelete = () => setPendingDeleteId(null);

  return {
    add: { open: isAdding, start: startAdd, save: saveAdd, cancel: cancelAdd },
    edit: { ids: editingIds, start: startEdit, save: saveEdit, cancel: cancelEdit },
    del: { pendingId: pendingDeleteId, start: startDelete, confirm: confirmDelete, cancel: cancelDelete },
    highlightId,
  };
};
